import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnInit,
    Output,
    SimpleChanges,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { SelectionModel } from '@angular/cdk/collections';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';

import { Unit, UnitsResponse } from '../../../../models/entities/unit';
import { Tag } from '../../../../models/entities/tag';
import { UnitsService } from '../../../../services/units/units.service';
import { LegFiJwtService } from '../../../../services/auth/legfi-jwt.service';
import { JwtLegFiClaims } from '../../../../services/auth/jwt-legfi-claims.model';
import { TagsDataService } from '../../../../services/tags/tags-data.service';
import { TagSelection } from '../../../../models/entities/tag-selection';
import { Ballot } from '../../../../models/entities/voting/ballot';
import { WeightedVotingCategory } from '../../../../models/entities/voting/weighted-voting-category';
import { LobConsolidatedMailOptions } from '../../../../models/entities/lob-mail-options';
import { LegfiCurrencyPipe } from '../../../../pipes/format/legfi-currency.pipe';

@UntilDestroy()
@Component({
    selector: 'app-v2-unit-selector',
    standalone: true,
    templateUrl: './v2-unit-selector.component.html',
    styleUrls: ['./v2-unit-selector.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CommonModule,
        FormsModule,
        MatProgressBarModule,
        LegfiCurrencyPipe,
    ],
})
export class V2UnitSelectorComponent implements OnInit, AfterViewInit, OnChanges {

    // --- Inputs ---
    /** Optional card title (e.g. "Units *"). When set, the selector renders as its own
     *  V2 card (title + divider + card chrome) — do NOT also wrap it in a mat-card. */
    @Input() cardTitle: string = null;
    @Input() options: LobConsolidatedMailOptions;
    @Input() selectionMode = true;
    @Input() isSingleUnit = false;
    @Input() preselectedUnitIds: number[] = [];
    @Input() selectedUnitIds: number[] = [];
    @Input() alreadySelectedUnits: Unit[] = [];
    @Input() onlyUnitIds: number[] = [];
    @Input() isDisplayedInModal = false;
    @Input() memberId: number;
    @Input() doHideMillCreekLocked = false;
    @Input() initialFilterPanelOpen = false;

    // Voting inputs (kept for compatibility)
    @Input() useVotingStyle: boolean = false;
    @Input() weightedVotingCategories: WeightedVotingCategory[] = [];
    @Input() ballotVotingUnitIds: number[] = [];
    @Input() ballot: Ballot;
    @Input() onlyBallotVotingUnits: boolean = false;
    @Input() creatingOrEditingWeightedVotingCategory: boolean = false;
    @Input() onlyWeightedVotingCategoryOrNull: number = null;
    @Input() useWeightedVotingCategories: boolean = false;

    // --- Outputs ---
    @Output() errorLoadingUnits = new EventEmitter<boolean>();
    @Output() onSelect = new EventEmitter<Unit[]>();
    @Output() onVotingSelect = new EventEmitter<number[]>();
    @Output() onSingleSelect = new EventEmitter<Unit>();
    @Output() onSelectAll = new EventEmitter<boolean>();

    // --- State ---
    units: Unit[] = [];
    filteredUnits: Unit[] = [];
    selection: SelectionModel<Unit>;
    selectedUnitTags: TagSelection = new TagSelection(true, []);
    selectedOwnerTags: TagSelection = new TagSelection(true, []);
    searchString = '';
    totalRecords = 0;
    originalRecords = 0;
    total: number = 0;

    // Filter panel
    filterPanelOpen = false;
    balanceFilter = ''; // kept for backwards compat
    balanceFilters = new Set<string>();
    balanceDropdownOpen = false;

    // Sorting
    sortColumn: string = '';
    sortDirection: 'asc' | 'desc' = 'asc';

    // UI pagination (client-side, for performance with large unit lists)
    pageSize = 400;
    currentPage = 0;
    pagedUnits: any[] = [];

    readonly balanceOptions: { value: string; label: string }[] = [
        { value: 'has-balance', label: 'Has balance' },
        { value: 'has-past-due', label: 'Has past due balance' },
        { value: 'zero', label: 'Zero balance' },
    ];

    // Tag dropdowns
    availableUnitTags: Tag[] = [];
    availableOwnerTags: Tag[] = [];
    unitTagDropdownOpen = false;
    ownerTagDropdownOpen = false;

    // Loading
    isLoadingUnits = true;
    loadingPreselected = false;
    viewInitialized = false;
    $loadingComplete = new Subject<boolean>();
    interruptLoading: Subject<void> = new Subject<void>();

    // Permissions
    hasMemberAccess = false;
    hasUnitReadAccess = false;
    hasUnitWriteAccess = false;
    isSuper = false;
    canSeeBalanceFields = false;

    // Voting
    weightedVotingCategoryIds: number[] = [];
    weightedVotingCategoryIdSelections: number[] = [];
    selectedWeightedVotingCategories: SelectionModel<WeightedVotingCategory> = new SelectionModel<WeightedVotingCategory>(true, []);

    showDeleted = false;

    private jwt: JwtLegFiClaims;
    private searchDebounce: Subject<string> = new Subject<string>();

    constructor(
        private _cd: ChangeDetectorRef,
        public _unitsService: UnitsService,
        private _tagsDataService: TagsDataService,
    ) {}

    // --- Options getter ---

    private get apiOptions(): {
        unitTags: string[];
        withoutUnitTags: string[];
        ownerTags: string[];
        withoutOwnerTags: string[];
        showDeleted: boolean;
        searchBy: string[];
    } {
        return {
            unitTags: this.selectedUnitTags.selected.filter((tag: Tag) => tag.filter === 'equals').map(t => t.tag),
            withoutUnitTags: this.selectedUnitTags.selected.filter((tag: Tag) => tag.filter === 'not_equals').map(t => t.tag),
            ownerTags: this.selectedOwnerTags.selected.filter((tag: Tag) => tag.filter === 'equals').map(t => t.tag),
            withoutOwnerTags: this.selectedOwnerTags.selected.filter((tag: Tag) => tag.filter === 'not_equals').map(t => t.tag),
            showDeleted: this.showDeleted,
            searchBy: [], // search all fields
        };
    }

    // --- Recompute filtered units (call when units or balanceFilter changes) ---

    private recomputeFilteredUnits(): void {
        let result: any[];
        if (this.balanceFilters.size === 0) {
            result = [...this.units];
        } else {
            result = this.units.filter(u => {
                // OR logic — unit passes if it matches ANY active balance filter
                for (const f of this.balanceFilters) {
                    switch (f) {
                        case 'has-balance': if (u.balance > 0) { return true; } break;
                        case 'has-past-due': if (u.pastDueBalance > 0) { return true; } break;
                        case 'zero': if (u.balance === 0 && u.pastDueBalance === 0) { return true; } break;
                    }
                }
                return false;
            });
        }

        // Apply sorting
        if (this.sortColumn) {
            const dir = this.sortDirection === 'asc' ? 1 : -1;
            result.sort((a, b) => {
                const aVal = a[this.sortColumn] ?? '';
                const bVal = b[this.sortColumn] ?? '';
                if (typeof aVal === 'number' && typeof bVal === 'number') {
                    return (aVal - bVal) * dir;
                }
                return String(aVal).localeCompare(String(bVal)) * dir;
            });
        }

        this.filteredUnits = result;
        this.currentPage = 0;
        this.recomputePagedUnits();
    }

    private recomputePagedUnits(): void {
        const start = this.currentPage * this.pageSize;
        this.pagedUnits = this.filteredUnits.slice(start, start + this.pageSize);
    }

    get totalPages(): number {
        return Math.ceil(this.filteredUnits.length / this.pageSize);
    }

    get showPagination(): boolean {
        return this.filteredUnits.length > this.pageSize;
    }

    goToPage(page: number): void {
        if (page < 0 || page >= this.totalPages) { return; }
        this.currentPage = page;
        this.recomputePagedUnits();
        this._cd.markForCheck();
    }

    // --- Active filter count ---

    get activeFilterCount(): number {
        let count = 0;
        count += this.balanceFilters.size;
        count += this.selectedUnitTags.selected.length;
        count += this.selectedOwnerTags.selected.length;
        return count;
    }

    // --- Lifecycle ---

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['options'] && !changes['options'].firstChange && changes['options'].currentValue) {
            const previousOptions = changes['options'].previousValue;
            const currentOptions = changes['options'].currentValue;

            if (previousOptions?.excludeZeroBalanceUnits !== currentOptions?.excludeZeroBalanceUnits) {
                this.selection.clear();
                this.getUnitList(true);

                if (this.preselectedUnitIds.length > 0) {
                    this.loadPreselectedUnits();
                }
            }
        }

        if (changes['preselectedUnitIds'] && !changes['preselectedUnitIds'].firstChange) {
            if (this.units?.length > 0) {
                const currentSelectedIds = this.selection.selected.map(u => u.id).sort();
                const preselectedSorted = [...this.preselectedUnitIds].sort();
                const alreadyMatches = currentSelectedIds.length === preselectedSorted.length &&
                    currentSelectedIds.every((id, index) => id === preselectedSorted[index]);

                if (!alreadyMatches) {
                    this.selection.clear();
                    for (const unit of this.units) {
                        if (this.preselectedUnitIds.includes(unit.id)) {
                            this.selection.select(unit);
                        }
                    }
                    this.emitSelectedUnits();
                }
            }
        }
    }

    ngOnInit(): void {
        this.jwt = LegFiJwtService.read();
        this.selection = new SelectionModel<Unit>(!this.isSingleUnit, []);
        this.filterPanelOpen = this.initialFilterPanelOpen;

        if (this.useVotingStyle) {
            this.alreadySelectedUnits.forEach((unit: Unit) => {
                if (!this.selection.isSelected(unit)) {
                    this.selection.select(unit);
                    if (!this.selectedUnitIds.includes(unit.id)) {
                        this.selectedUnitIds = [...this.selectedUnitIds, unit.id];
                    }
                }
            });
        }

        this._tagsDataService.getOrganizationTagsByType('Unit').pipe(untilDestroyed(this)).subscribe();
        this._tagsDataService.getOrganizationTagsByType('Owner').pipe(untilDestroyed(this)).subscribe();

        // Subscribe to available tags for dropdown lists
        this._tagsDataService.getObservableByType('Unit').pipe(untilDestroyed(this)).subscribe((tags) => {
            this.availableUnitTags = tags;
            this._cd.markForCheck();
        });
        this._tagsDataService.getObservableByType('Owner').pipe(untilDestroyed(this)).subscribe((tags) => {
            this.availableOwnerTags = tags;
            this._cd.markForCheck();
        });

        this.hasMemberAccess = LegFiJwtService.doesUserHaveModulePermission('members', true);
        this.hasUnitReadAccess = LegFiJwtService.doesUserHaveModulePermission('unit', false);
        this.hasUnitWriteAccess = LegFiJwtService.doesUserHaveModulePermission('unit', true);
        this.isSuper = this.jwt.superUser;

        const hasChargesPermission = LegFiJwtService.doesUserHaveModulePermission('charges', false);
        const hasUnitsPermission = LegFiJwtService.doesUserHaveModulePermission('unit', false);
        this.canSeeBalanceFields = hasChargesPermission || hasUnitsPermission;

        // Subscribe to units store
        this._unitsService.$units.pipe(untilDestroyed(this)).subscribe((units) => {
            if (this.jwt.orgId === 4203 && this.doHideMillCreekLocked) {
                units = units.filter(u => !u.owners.find(m => m.membership.locked));
            }
            this.units = units;
            this.totalRecords = units.length;
            this.recomputeFilteredUnits();
            this._cd.markForCheck();
        });

        this._unitsService.reloadUnitList$.pipe(untilDestroyed(this)).subscribe((reset) => this.getUnitList(reset));

        // Preselected units loading
        if (this.preselectedUnitIds.length > 0) {
            this.loadingPreselected = true;
            this._unitsService.resetStore();
            const options = {
                ...this.apiOptions,
                searchOr: true,
                unitIds: this.onlyUnitIds,
                weightedVotingCategoryIds: this.weightedVotingCategoryIds,
                isVotingSearch: this.useVotingStyle,
                ballotId: this.ballot?.id,
                onlyBallotVotingUnits: this.onlyBallotVotingUnits,
                excludeZeroBalanceUnits: this.options?.excludeZeroBalanceUnits,
            };
            if (this.creatingOrEditingWeightedVotingCategory) {
                options['onlyWeightedVotingCategoryOrNull'] = this.onlyWeightedVotingCategoryOrNull;
            }
            this._unitsService.getUnits(
                null,
                this.preselectedUnitIds.join(' '),
                'title',
                'asc',
                1,
                this.preselectedUnitIds.length,
                options,
            ).pipe(untilDestroyed(this)).subscribe((response: UnitsResponse) => {
                response.data.forEach((unit: Unit) => {
                    this.selection.select(unit);
                });
                this.loadingPreselected = false;
                this.emitSelectedUnits();
                if (this.viewInitialized) {
                    this.getUnitList(false);
                }
            });
        }

        // Debounced search
        this.searchDebounce.pipe(debounceTime(300), untilDestroyed(this)).subscribe((term) => {
            this.searchString = term;
            this.getUnitList();
        });
    }

    ngAfterViewInit(): void {
        if (!this.loadingPreselected) {
            this.getUnitList(this.preselectedUnitIds.length === 0);
        }
        this.viewInitialized = true;
        this._cd.detectChanges();
    }

    // --- Search ---

    onSearchInput(term: string): void {
        this.searchDebounce.next(term);
    }

    clearSearch(): void {
        this.searchString = '';
        this.getUnitList();
    }

    // --- Progressive Loading ---

    getUnitList(reset = true): void {
        this.isLoadingUnits = true;
        this._cd.markForCheck();
        if (reset) {
            this._unitsService.resetStore();
        }
        this.interruptLoading.next();
        const options = {
            ...this.apiOptions,
            unitIds: this.onlyUnitIds,
            weightedVotingCategoryIds: this.weightedVotingCategoryIds,
            isVotingSearch: this.useVotingStyle,
            ballotId: this.ballot?.id,
            onlyBallotVotingUnits: this.onlyBallotVotingUnits,
            excludeZeroBalanceUnits: this.options?.excludeZeroBalanceUnits,
        };
        if (this.creatingOrEditingWeightedVotingCategory) {
            options['onlyWeightedVotingCategoryOrNull'] = this.onlyWeightedVotingCategoryOrNull;
        }
        this._unitsService.getUnits(
            null,
            this.searchString,
            'name',
            'asc',
            1,
            500,
            options,
        ).pipe(untilDestroyed(this), takeUntil(this.interruptLoading))
            .subscribe({
                next: (response: UnitsResponse) => {
                    this.total = response.meta.total;
                    this.totalRecords = response.meta.total;
                    this.originalRecords = response.meta.original;
                    this.recomputeFilteredUnits();
                    this._cd.markForCheck(); // Show first page data + loading progress immediately
                    if (response.data.length === 500 && this.units.length < response.meta.total) {
                        this.getNextPage(1, 500);
                    } else {
                        this.isLoadingUnits = false;
                        this._cd.markForCheck();
                    }
                },
                error: () => {
                    this.errorLoadingUnits.emit(true);
                    this.isLoadingUnits = false;
                    this._cd.markForCheck();
                },
            });
    }

    getNextPage(lastPage: number, perPage: number): void {
        const options = {
            ...this.apiOptions,
            unitIds: this.onlyUnitIds,
            weightedVotingCategoryIds: this.weightedVotingCategoryIds,
            isVotingSearch: this.useVotingStyle,
            ballotId: this.ballot?.id,
            onlyBallotVotingUnits: this.onlyBallotVotingUnits,
            excludeZeroBalanceUnits: this.options?.excludeZeroBalanceUnits,
        };
        if (this.creatingOrEditingWeightedVotingCategory) {
            options['onlyWeightedVotingCategoryOrNull'] = this.onlyWeightedVotingCategoryOrNull;
        }
        this._unitsService.getUnits(
            null,
            this.searchString,
            'name',
            'asc',
            lastPage + 1,
            perPage,
            options,
        ).pipe(untilDestroyed(this), takeUntil(this.interruptLoading))
            .subscribe((response: UnitsResponse) => {
                this.total = response.meta.total;
                this.totalRecords = response.meta.total;
                this.recomputeFilteredUnits();
                this._cd.markForCheck(); // Update loading bar progress on each page
                if (response.data.length === perPage && this.units.length < response.meta.total) {
                    this.getNextPage(lastPage + 1, perPage);
                } else {
                    this.isLoadingUnits = false;
                    this.$loadingComplete.next(true);
                    this._cd.markForCheck();
                }
            });
    }

    // --- Selection ---

    isSelected(unit: Unit): boolean {
        return this.selectedUnitIds.includes(unit.id);
    }

    isAllSelected(): boolean {
        if (!this.selection?.selected.length || this.filteredUnits.length === 0) {
            return false;
        }
        const selectedSet = new Set(this.selectedUnitIds);
        return this.filteredUnits.every(u => selectedSet.has(u.id));
    }

    isSomeSelected(): boolean {
        if (!this.selection?.selected.length || this.filteredUnits.length === 0) {
            return false;
        }
        const selectedSet = new Set(this.selectedUnitIds);
        const someSelected = this.filteredUnits.some(u => selectedSet.has(u.id));
        return someSelected && !this.isAllSelected();
    }

    toggleRow(unit: Unit): void {
        if (this.selectedUnitIds.indexOf(unit.id) !== -1 && !this.selection.isSelected(unit)) {
            // Object reference mismatch from pagination — deselect by ID
            this.selection.selected.forEach((u) => {
                if (u.id === unit.id) {
                    this.selection.deselect(u);
                    this.onSingleSelect.emit(u);
                }
            });
        } else {
            this.selection.toggle(unit);
            this.onSingleSelect.emit(unit);
        }
        this.emitSelectedUnits();
    }

    toggleAll(): void {
        if (this.isAllSelected()) {
            this.deselectAll();
        } else {
            // Select all currently loaded & filtered units (no blocking dialog)
            this.filteredUnits.forEach(u => this.selection.select(u));
            this.emitSelectedUnits();
            this.onSelectAll.emit(true);
            this._cd.markForCheck();
        }
    }

    deselectAll(): void {
        this.selection.clear();
        this.emitSelectedUnits();
        this.onSelectAll.emit(false);
        this._cd.markForCheck();
    }

    selectAllVisible(): void {
        this.filteredUnits.forEach(u => this.selection.select(u));
        this.emitSelectedUnits();
        this._cd.markForCheck();
    }

    selectAllWithBalance(): void {
        this.filteredUnits.filter(u => u.balance > 0).forEach(u => this.selection.select(u));
        this.emitSelectedUnits();
        this._cd.markForCheck();
    }

    emitSelectedUnits(): void {
        this.selectedUnitIds = this.selection.selected.map(u => u.id);
        this.onSelect.emit(this.selection.selected);
    }

    // --- Filter panel ---

    toggleFilterPanel(): void {
        this.filterPanelOpen = !this.filterPanelOpen;
    }

    onBalanceFilterChange(value: string): void {
        // Legacy single-select path (unused but kept for safety)
        this.balanceFilter = value;
        this.recomputeFilteredUnits();
        this._cd.markForCheck();
    }

    toggleBalanceFilter(value: string): void {
        if (this.balanceFilters.has(value)) {
            this.balanceFilters.delete(value);
        } else {
            // "zero" is mutually exclusive with "has-balance" and "has-past-due"
            if (value === 'zero') {
                this.balanceFilters.delete('has-balance');
                this.balanceFilters.delete('has-past-due');
            } else if (value === 'has-balance' || value === 'has-past-due') {
                this.balanceFilters.delete('zero');
            }
            this.balanceFilters.add(value);
        }
        this.balanceFilters = new Set(this.balanceFilters); // new ref for OnPush
        this.recomputeFilteredUnits();
        this._cd.markForCheck();
    }

    isBalanceFilterActive(value: string): boolean {
        return this.balanceFilters.has(value);
    }

    removeBalanceFilterChip(value: string): void {
        this.balanceFilters.delete(value);
        this.balanceFilters = new Set(this.balanceFilters);
        this.recomputeFilteredUnits();
        this._cd.markForCheck();
    }

    toggleBalanceDropdown(): void {
        this.balanceDropdownOpen = !this.balanceDropdownOpen;
        this.unitTagDropdownOpen = false;
        this.ownerTagDropdownOpen = false;
    }

    getBalanceFilterLabel(value: string): string {
        const opt = this.balanceOptions.find(o => o.value === value);
        return opt?.label ?? value;
    }

    clearAllFilters(): void {
        this.balanceFilters = new Set();
        this.balanceFilter = '';
        this.selectedUnitTags.clear();
        this.selectedOwnerTags.clear();
        this.recomputeFilteredUnits();
        this.getUnitList();
    }

    toggleSort(column: string): void {
        if (this.sortColumn === column) {
            this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
        } else {
            this.sortColumn = column;
            this.sortDirection = 'asc';
        }
        this.recomputeFilteredUnits();
        this._cd.markForCheck();
    }

    removeBalanceFilter(): void {
        this.balanceFilters = new Set();
        this.balanceFilter = '';
        this.recomputeFilteredUnits();
        this._cd.markForCheck();
    }

    // --- Tag dropdown methods ---

    toggleUnitTagDropdown(): void {
        this.unitTagDropdownOpen = !this.unitTagDropdownOpen;
        this.ownerTagDropdownOpen = false;
        this.balanceDropdownOpen = false;
    }

    toggleOwnerTagDropdown(): void {
        this.ownerTagDropdownOpen = !this.ownerTagDropdownOpen;
        this.unitTagDropdownOpen = false;
        this.balanceDropdownOpen = false;
    }

    closeAllDropdowns(): void {
        this.unitTagDropdownOpen = false;
        this.ownerTagDropdownOpen = false;
        this.balanceDropdownOpen = false;
    }

    // Tri-state tag toggle: off → has (include) → not (exclude) → off
    toggleUnitTag(tag: Tag): void {
        const existing = this.selectedUnitTags.selected.find(t => t.tag === tag.tag);
        if (!existing) {
            // off → has
            this.selectedUnitTags.select(new Tag({ tag: tag.tag, filter: 'equals' }));
        } else if (existing.filter === 'equals') {
            // has → not
            this.selectedUnitTags.deselect(existing);
            this.selectedUnitTags.select(new Tag({ tag: tag.tag, filter: 'not_equals' }));
        } else {
            // not → off
            this.selectedUnitTags.deselect(existing);
        }
        this.getUnitList();
    }

    toggleOwnerTag(tag: Tag): void {
        const existing = this.selectedOwnerTags.selected.find(t => t.tag === tag.tag);
        if (!existing) {
            this.selectedOwnerTags.select(new Tag({ tag: tag.tag, filter: 'equals' }));
        } else if (existing.filter === 'equals') {
            this.selectedOwnerTags.deselect(existing);
            this.selectedOwnerTags.select(new Tag({ tag: tag.tag, filter: 'not_equals' }));
        } else {
            this.selectedOwnerTags.deselect(existing);
        }
        this.getUnitList();
    }

    getUnitTagState(tagName: string): 'off' | 'has' | 'not' {
        const existing = this.selectedUnitTags.selected.find(t => t.tag === tagName);
        if (!existing) { return 'off'; }
        return existing.filter === 'equals' ? 'has' : 'not';
    }

    getOwnerTagState(tagName: string): 'off' | 'has' | 'not' {
        const existing = this.selectedOwnerTags.selected.find(t => t.tag === tagName);
        if (!existing) { return 'off'; }
        return existing.filter === 'equals' ? 'has' : 'not';
    }

    // Toggle chip between has ↔ not (clicking the chip body)
    toggleChipMode(tag: any, type: 'unit' | 'owner'): void {
        const tagSelection = type === 'unit' ? this.selectedUnitTags : this.selectedOwnerTags;
        const existing = tagSelection.selected.find(t => t.tag === tag.tag);
        if (existing) {
            const newFilter = existing.filter === 'equals' ? 'not_equals' : 'equals';
            tagSelection.deselect(existing);
            tagSelection.select(new Tag({ tag: tag.tag, filter: newFilter }));
            this.getUnitList();
        }
    }

    get unitTagInputText(): string {
        return this.selectedUnitTags.selected
            .map(t => t.filter === 'not_equals' ? 'not ' + t.tag : t.tag)
            .join(', ');
    }

    get ownerTagInputText(): string {
        return this.selectedOwnerTags.selected
            .map(t => t.filter === 'not_equals' ? 'not ' + t.tag : t.tag)
            .join(', ');
    }

    removeUnitTag(tag: any): void {
        const existing = this.selectedUnitTags.selected.find(t => t.tag === tag.tag);
        if (existing) {
            this.selectedUnitTags.deselect(existing);
            this.getUnitList();
        }
    }

    removeOwnerTag(tag: any): void {
        const existing = this.selectedOwnerTags.selected.find(t => t.tag === tag.tag);
        if (existing) {
            this.selectedOwnerTags.deselect(existing);
            this.getUnitList();
        }
    }

    // --- Search highlighting ---

    highlight(text: string): string {
        if (!text || !this.searchString) {
            return this.escapeHtml(text || '');
        }
        const q = this.searchString.trim();
        if (!q) {
            return this.escapeHtml(text);
        }
        // Escape HTML entities first to prevent XSS from server data
        const safe = this.escapeHtml(text);
        const escaped = q.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        return safe.replace(new RegExp(`(${escaped})`, 'gi'), '<mark>$1</mark>');
    }

    private escapeHtml(text: string): string {
        return text
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;');
    }

    getMatchSource(unit: Unit): string | null {
        if (!this.searchString) {
            return null;
        }
        const q = this.searchString.toLowerCase().trim();
        if (!q) {
            return null;
        }
        // Check if the match is already visible in the table columns
        const visibleFields = [
            unit.name,
            unit.address?.line1,
            unit.address?.city,
            unit.address?.region,
            unit.address?.postalCode,
            ...unit.owners.map(o => o.name),
            ...(unit.tags || []).map(t => t.tag || ''),
        ].filter(Boolean);
        if (visibleFields.some(f => f.toLowerCase().includes(q))) {
            return null;
        }
        // Match is from a non-visible field — find which one
        const emails = unit.owners
            .filter(o => o.membership?.user?.email)
            .map(o => o.membership.user.email);
        const matchEmail = emails.find(e => e.toLowerCase().includes(q));
        if (matchEmail) {
            return 'Email: ' + matchEmail;
        }
        if (unit.description?.toLowerCase().includes(q)) {
            return 'Description match';
        }
        return null;
    }

    // --- Tag color helpers ---

    tagColorClass(tag: any): string {
        const name = (tag.tag || '').toLowerCase();
        if (name.includes('delinquent')) { return 't-del'; }
        if (name.includes('rent')) { return 't-rent'; }
        if (name.includes('board')) { return 't-board'; }
        if (name.includes('vacant')) { return 't-vacant'; }
        return '';
    }

    // --- TrackBy ---

    trackByUnitId(index: number, unit: Unit): number {
        return unit.id;
    }

    trackByOwnerId(index: number, owner: any): number | string {
        return owner.id ?? `idx-${index}`;
    }

    trackByTagId(index: number, tag: any): number | string {
        return tag.id ?? tag.tag ?? `idx-${index}`;
    }

    trackByBalanceOption(index: number, opt: { value: string; label: string }): string {
        return opt.value;
    }

    trackByBalanceFilter(index: number, value: string): string {
        return value;
    }

    // --- Private methods ---

    private loadPreselectedUnits(): void {
        this.loadingPreselected = true;
        this._unitsService.resetStore();
        const options = {
            ...this.apiOptions,
            searchOr: true,
            unitIds: this.onlyUnitIds,
            weightedVotingCategoryIds: this.weightedVotingCategoryIds,
            isVotingSearch: this.useVotingStyle,
            ballotId: this.ballot?.id,
            onlyBallotVotingUnits: this.onlyBallotVotingUnits,
            excludeZeroBalanceUnits: this.options?.excludeZeroBalanceUnits,
        };
        if (this.creatingOrEditingWeightedVotingCategory) {
            options['onlyWeightedVotingCategoryOrNull'] = this.onlyWeightedVotingCategoryOrNull;
        }
        this._unitsService.getUnits(
            null,
            this.preselectedUnitIds.join(' '),
            'title',
            'asc',
            1,
            this.preselectedUnitIds.length,
            options,
        ).pipe(untilDestroyed(this)).subscribe((response: UnitsResponse) => {
            response.data.forEach((unit: Unit) => {
                this.selection.select(unit);
            });
            this.loadingPreselected = false;
            this.emitSelectedUnits();
        });
    }

}
